/* DeniedState — professional permission-denied UX.
   Shown when a user lacks the required capability for a page or section.
   Reason-aware: adapts copy to whether the denial is a permission issue,
   plan restriction, or org policy. */
import React from 'react';
import { NexusIcon } from '../../shared/ui/NexusIcon';
import { Btn } from '../../shared/ui';

const CONFIGS = {
  insufficient_permission: {
    icon:    'ShieldOff',
    color:   '#7C8FA8',
    heading: 'Insufficient Permission',
    sub:     'Your current role does not grant access to this area. Contact your Organization Admin to request elevated permissions.',
  },
  plan_restriction: {
    icon:    'Zap',
    color:   '#F59E0B',
    heading: 'Plan Restriction',
    sub:     'This feature is not available on your current plan. Upgrade your subscription to unlock it.',
  },
  org_policy: {
    icon:    'Building2',
    color:   '#6E3FBA',
    heading: 'Organization Policy',
    sub:     'Access to this area has been restricted by your Organization Administrator.',
  },
  read_only: {
    icon:    'Lock',
    color:   '#7C8FA8',
    heading: 'Read-Only Access',
    sub:     'You have view-only access to this resource. Actions are disabled.',
  },
  system_role_locked: {
    icon:    'Shield',
    color:   'var(--nx-accent, #125AB0)',
    heading: 'System Role',
    sub:     'System roles cannot be modified. Duplicate this role to create a customizable copy.',
  },
};

export function DeniedState({ reason = 'insufficient_permission', message, action, actionLabel, compact = false }) {
  const cfg = CONFIGS[reason] ?? CONFIGS.insufficient_permission;

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-1, #1A1F2E)', border: '1px solid var(--nx-border)' }}>
        <NexusIcon name={cfg.icon} size={14} style={{ color: cfg.color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{message ?? cfg.sub}</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '56px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ '--_dc': cfg.color, width: 52, height: 52, borderRadius: '50%', background: 'color-mix(in srgb, var(--_dc) 9%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NexusIcon name={cfg.icon} size={24} style={{ color: 'var(--_dc)' }} />
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 6 }}>{cfg.heading}</div>
        <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>{message ?? cfg.sub}</div>
      </div>
      {action && (
        <Btn variant="secondary" onClick={action}>{actionLabel ?? 'Learn More'}</Btn>
      )}
    </div>
  );
}

/* Inline denied pill — shown next to a disabled element */
export function DeniedPill({ reason = 'insufficient_permission' }) {
  const labels = {
    insufficient_permission: 'No permission',
    plan_restriction:        'Upgrade required',
    org_policy:              'Restricted by policy',
    read_only:               'View only',
  };
  return (
    <span title={CONFIGS[reason]?.sub} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(124,143,168,0.12)', color: '#7C8FA8', fontWeight: 600, letterSpacing: '0.03em', cursor: 'help' }}>
      {labels[reason] ?? 'No permission'}
    </span>
  );
}
